<script setup>
  import { computed } from 'vue'
  import BcstProgressBar from './BcstProgressBar.vue'
  import BcsDynamicText from '../BcsDynamicText.vue'
  import { useTransformLearningStore } from '../../stores/TransformLearningStore'
  import certificationIcon from '@/assets/images/bcs-certification-icon.svg'
  import certificationCompletedIcon from '@/assets/images/bcst-certification-completed-icon.svg'

  const emit = defineEmits(['viewProgressDetails'])

  const store = useTransformLearningStore()

  const isZeroProgress = computed(() => store.overallPercent === 0)
  const isFullyCompleted = computed(() => store.overallPercent === 100)

  function handleViewProgressDetails() {
    emit('viewProgressDetails')
  }
</script>

<template>
  <div class="card tpc-card">
    <div class="card-header bg-light tpc-header">
      <div class="d-flex justify-content-between align-items-center">
        <p class="header-20 mb-0"><strong>Your Progress</strong></p>
        <span
          class="words-for-definition no-close"
          role="button"
          tabindex="0"
          @click="handleViewProgressDetails"
          @keydown.enter.prevent="handleViewProgressDetails"
          @keydown.space.prevent="handleViewProgressDetails">
          <BcsDynamicText text="bcstLearningDashboardViewProgressDetails" />
        </span>
      </div>
    </div>

    <div class="card-body tpc-body d-flex flex-column justify-content-center">
      <BcstProgressBar
        :value="store.overallPercent"
        size="lg"
        class="tpc-progress">
        <template #label>
          <span
            class="d-block tpc-percent tpc-percent-label"
            :class="isZeroProgress ? 'text-secondary' : 'text-success'">
            {{ store.overallPercent }}% Complete
          </span>
        </template>
        <template #icon>
          <img
            :src="isFullyCompleted ? certificationCompletedIcon : certificationIcon"
            :alt="isFullyCompleted ? 'BCS Certification completed badge' : 'BCS Certification badge'"
            class="tpc-badge flex-shrink-0"
            :class="{ 'tpc-badge--completed': isFullyCompleted }" />
        </template>
        <template #sublabel>
          <p class="text-16 text-success mb-0 tpc-remaining">
            <med v-if="!isFullyCompleted">
              {{ store.lessonsRemaining }} lessons remaining to unlock your certification!
            </med>
            <med v-else>
              Well done! You've earned your BCS Compliance Certification.
            </med>
          </p>
        </template>
      </BcstProgressBar>
    </div>
  </div>
</template>

<style scoped lang="scss">
  .tpc-card {
    height: 192px;
    border-radius: 4px;
    border: 1px solid var(--bs-gray-300);
  }

  .tpc-header {
    padding: 14px 27px;
    border-radius: 3px 3px 0 0;
  }

  .tpc-body {
    padding: 14px 27px;
  }

  .tpc-percent {
    margin-bottom: 12px;
  }

  /* Figma: 20px / 700 / 125% / -0.4px letter-spacing */
  .tpc-percent-label {
    font-family: 'DM Sans', sans-serif;
    font-size: 20px;
    font-style: normal;
    font-weight: 700;
    line-height: 125%;
    letter-spacing: -0.4px;
  }

  /* Bar row height pinned to 22px so the badge overflows without expanding the row */
  .tpc-progress :deep(.bpb-bar-row) {
    height: 22px;
  }

  /* Figma: track always gray-200 background, 20px radius (overrides BcsLinearProgress defaults) */
  .tpc-progress :deep(.progress) {
    border-radius: 20px;
    background-color: var(--bs-gray-200);
    box-shadow: none;
  }

  .tpc-progress :deep(.progress-bar) {
    border-radius: 20px;
  }

  /* Negative margin-left = half the badge width → badge center aligns with bar's right edge */
  .tpc-badge {
    width: 64px;
    height: auto;
    margin-left: -32px;
  }

  /* Completed badge sized 45% larger than the default badge (64px * 1.45 = 92.8px) */
  .tpc-badge--completed {
    width: 92.8px;
    margin-left: -46.4px;
  }

  .tpc-remaining {
    margin-top: 14px;
    letter-spacing: -0.32px;
  }
</style>
